import {
  QUICK_FACTS_AND_GOALS_MAX_ITEM_LENGTH,
  QUICK_FACTS_AND_GOALS_MAX_ITEMS,
  type QuickFactsAndGoalsListResponse,
} from "../../../shared/api-types";

// Quick facts and Goals on the patient page (NO-852): what each card reads,
// how its edit view behaves, and how a save's answer is read. Pure and
// relative-import only, so `tsx --test` runs it. Query keys and request paths
// hold nothing but the Client id; list text never leaves the card except in
// the PUT body.

export type ListKind = "quick_facts" | "goals";

/** A save's answer as a card reads it; the page's save maps the HTTP answer to it. */
export type ListSaveResult =
  | { status: "saved" }
  | { status: "conflict"; current: QuickFactsAndGoalsListResponse }
  | { status: "failed" };

/** A save's error as a card reads it: a 409's current list, else a failure. */
export function saveFailureResult(error: unknown): ListSaveResult {
  const current = conflictCurrent(error);

  return current ? { status: "conflict", current } : { status: "failed" };
}

/**
 * The list the page's cache should hold after a failed save: a conflict's
 * current list, so Cancel shows what is stored now; null leaves the cache.
 */
export function listToCacheAfterFailure(
  result: ListSaveResult
): QuickFactsAndGoalsListResponse | null {
  return result.status === "conflict" ? result.current : null;
}

export const MAX_ITEMS = QUICK_FACTS_AND_GOALS_MAX_ITEMS;
export const MAX_ITEM_LENGTH = QUICK_FACTS_AND_GOALS_MAX_ITEM_LENGTH;

export const EMPTY_LIST_MESSAGE = "Nothing added yet.";
export const AT_CAP_MESSAGE = `You can keep up to ${MAX_ITEMS}.`;
export const SAVE_FAILED_MESSAGE = "Couldn't save. Try again.";
export const CONFLICT_TITLE = "This list changed while you were editing.";

/** Each card's own words. */
export const LIST_COPY: Record<
  ListKind,
  { title: string; addItem: string; loadFailed: string; bulleted: boolean }
> = {
  quick_facts: {
    title: "Quick facts",
    addItem: "Add a quick fact",
    loadFailed: "Couldn't load quick facts.",
    bulleted: true,
  },
  goals: {
    title: "Goals",
    addItem: "Add a goal",
    loadFailed: "Couldn't load goals.",
    bulleted: false,
  },
};

const PATH_SEGMENT: Record<ListKind, string> = {
  quick_facts: "quick-facts",
  goals: "goals",
};

export function listQueryKey(kind: ListKind, patientId: string): string[] {
  return [kind === "quick_facts" ? "patient-quick-facts" : "patient-goals", patientId];
}

export function listPath(kind: ListKind, patientId: string): string {
  return `/api/patients/${encodeURIComponent(patientId)}/${PATH_SEGMENT[kind]}`;
}

/** What the server stores: trimmed items, blank ones dropped. */
export function normalizeDraft(draft: readonly string[]): string[] {
  return draft.map((item) => item.trim()).filter((item) => item.length > 0);
}

/**
 * The current list a 409 carries, read from an API error's status and body
 * (duck-typed so this module imports no transport). Null for any other error.
 */
export function conflictCurrent(error: unknown): QuickFactsAndGoalsListResponse | null {
  const { status, body } = errorShape(error);
  if (status !== 409 || !body || typeof body !== "object") return null;

  const { code, current } = body as { code?: unknown; current?: unknown };
  if (code !== "patient_list_conflict" || !isList(current)) return null;

  return { items: current.items, version: current.version };
}

/** 403 and 404 mean "not this Client's RD": the card shows nothing. */
export function isHiddenLoadError(error: unknown): boolean {
  const { status } = errorShape(error);

  return status === 403 || status === 404;
}

/** What one card's slot shows: nothing, the neutral retry line, or the card. */
export type ListCardVisibility =
  | { kind: "hidden" }
  | { kind: "retry" }
  | { kind: "card"; list: QuickFactsAndGoalsListResponse };

/**
 * A card shows nothing without patient_quick_notes:read:own, while it loads,
 * or when its read answers 403 or 404 (not this Client's RD). Any other
 * failure is the retry line inside the card, never a silent hide.
 */
export function listCardVisibility(input: {
  canRead: boolean;
  isError: boolean;
  error: unknown;
  data: QuickFactsAndGoalsListResponse | undefined;
}): ListCardVisibility {
  if (!input.canRead) return { kind: "hidden" };

  if (input.isError) {
    return isHiddenLoadError(input.error) ? { kind: "hidden" } : { kind: "retry" };
  }

  return input.data ? { kind: "card", list: input.data } : { kind: "hidden" };
}

function errorShape(error: unknown): { status?: unknown; body?: unknown } {
  return error && typeof error === "object"
    ? (error as { status?: unknown; body?: unknown })
    : {};
}

function isList(value: unknown): value is QuickFactsAndGoalsListResponse {
  if (!value || typeof value !== "object") return false;

  const { items, version } = value as { items?: unknown; version?: unknown };
  const itemsOk =
    Array.isArray(items) && items.every((item) => typeof item === "string");

  return itemsOk && typeof version === "number";
}

// --- The edit view's state --------------------------------------------------

export interface ListEditorState {
  editing: boolean;
  draft: string[];
  baseVersion: number;
  saving: boolean;
  /** The server's list after a 409; the draft is kept until the RD chooses. */
  conflict: QuickFactsAndGoalsListResponse | null;
  failed: boolean;
}

export type ListEditorAction =
  | { type: "edit"; list: QuickFactsAndGoalsListResponse }
  | { type: "change"; index: number; value: string }
  | { type: "add" }
  | { type: "remove"; index: number }
  | { type: "cancel" }
  | { type: "save_started" }
  | { type: "saved" }
  | { type: "conflict"; current: QuickFactsAndGoalsListResponse }
  | { type: "failed" }
  | { type: "use_latest" };

export const READ_STATE: ListEditorState = {
  editing: false,
  draft: [],
  baseVersion: 0,
  saving: false,
  conflict: null,
  failed: false,
};

/**
 * Starting an edit copies the list (an empty list starts with one blank
 * item). A conflict never merges or overwrites: it keeps the draft and the
 * RD picks Use latest or Save mine instead.
 */
export function listEditorReducer(
  state: ListEditorState,
  action: ListEditorAction
): ListEditorState {
  switch (action.type) {
    case "edit":
      return startEditing(action.list);
    case "change":
      return { ...state, draft: replaceAt(state.draft, action.index, action.value) };
    case "add":
      return canAddItem(state.draft)
        ? { ...state, draft: [...state.draft, ""] }
        : state;
    case "remove":
      return {
        ...state,
        draft: state.draft.filter((_, index) => index !== action.index),
      };
    case "cancel":
    case "saved":
      return READ_STATE;
    case "save_started":
      return { ...state, saving: true, failed: false };
    case "conflict":
      return { ...state, saving: false, conflict: action.current };
    case "failed":
      return { ...state, saving: false, failed: true };
    case "use_latest":
      return state.conflict ? startEditing(state.conflict) : state;
  }
}

function startEditing(list: QuickFactsAndGoalsListResponse): ListEditorState {
  return {
    ...READ_STATE,
    editing: true,
    draft: list.items.length > 0 ? [...list.items] : [""],
    baseVersion: list.version,
  };
}

function replaceAt(draft: readonly string[], index: number, value: string): string[] {
  return draft.map((item, at) =>
    at === index ? value.slice(0, MAX_ITEM_LENGTH) : item
  );
}

export function canAddItem(draft: readonly string[]): boolean {
  return draft.length < MAX_ITEMS;
}

/** The base a save sends: the conflict's version when the RD chose Save mine instead. */
export function saveBase(state: ListEditorState, saveMine: boolean): number {
  return saveMine && state.conflict ? state.conflict.version : state.baseVersion;
}
